import { TreatmentTypes } from '../overview/models';



export interface OrderplanPeriodData {
  periodId: number;
  orderplanRequisiteSummaries: OrderplanRequisiteSummary[];
  manuallyAddedProducts: ManuallyAddedCropAid[];
}

export interface OrderplanRequisiteSummary {
  cropAidId: number;
  customerProductInventoryUsedAmount: number;
  orderLinesTotalAmount: number;
  usedOrderOverflowAmount: number;
  requisiteAmount: number;
}

export interface ManuallyAddedCropAid {
  cropAidId: number;
  orderLinesTotalAmount: number;
}

export interface OrderplanRequisiteDisplaySummary {
  cropAid: CropAid;
  orderLines: OrderLine[];
  customerProductInventoryUsedAmount: number;
  orderLinesTotalAmount: number;
  usedOrderOverflowAmount: number;
  requisiteAmount: number;
}

export interface ManuallyAddedCropAidDisplay {
  cropAid: CropAid;
  orderLines: OrderLine[];
  orderLinesTotalAmount: number;
}

export interface OrderplanProductData {
  cropAidId: number;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
}

export interface GetOrderLinesResponse {
  orderLines: OrderLine[];
}

export interface ImportStockFromTelersdashboardResponse {
  year: number;
  importedCount: number;
}

export interface GetCustomerProductInventoryResponse {
  productInventories: ProductInventoryData[];
}

export interface OrderLine {
  id: number;
  packageId: number;
  cropAidId?: number;
  count: number;
  year: number;
  periodId: number;
  status: OrderLineStatus;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
  treatmentType: TreatmentTypes;
  orderedDate: string;
  package?: Package;
  cropAid?: CropAid;
  shipToId: number;
  addedByOptimizer: boolean;
  /**
   * Newest version (2022) of tracking orderlines to the cart-service.
   */
  cartId?: number;
  /**
   * Old version (~2020-2021) of tracking orderlines to the checkout-service.
   */
  checkoutId?: number;
  /**
   * Oldest version (~2020) of tracking orderlines to the order-management-service.
   */
  orderedId?: number;
}

export interface RapportOrderLine extends OrderLine {
  cropAid?: CropAid;
  package?: Package;
}

export interface ReportOrderLine {
  cropAid?: CropAid;
  package?: Package;
  count: number;
  requisiteAmount?: number;
}

export enum OrderLineStatus {
  open = 'Open',
  inCart = 'InCart',
  finalized = 'Finalized',
}

export enum DeliveryPeriodMomentType {
  empty = 'Empty',
  beginning = 'Beginning',
  middle = 'Middle',
  end = 'End',
}

export enum DeliveryPreference {
  none = 'NONE',
  early = 'Y1',
  middle = 'Y2',
  late = 'Y3',
}

export interface DeliveryPeriodMoment {
  periodId: number;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
}

export interface SetDeliveryPeriodMomentsRequest {
  year: number;
  deliveryPeriodMoments: DeliveryPeriodMoment[];
}

export interface UpsertCustomerProductInventoryRequest {
  year: number;
  productInventories: ProductInventoryData[];
}

export interface ProductInventoryData {
  cropAidId: number;
  amount: number;
}

export enum SourceEnum {
  Manual = 'Manual',
  AgrifirmArableLegacySAP = 'AgrifirmArableLegacySAP',
  OneSAP = 'OneSAP',
}

export interface CropAid {
  id: number;
  productId: number;
  name: string;
  unitOfMeasurement: string;
  codes: string[];
  isActive: boolean;
  source: SourceEnum;
}

export interface GetPackagesResponse {
  packages: Package[];
}

export interface Package {
  id: number;
  unitOfMeasurement: string;
  productId: number;
  amount: number;
  isActive: boolean;
  name: string;
  packageVersion: PackageVersion;
}

export enum PackageVersion {
  V1 = 'V1',
  V2 = 'V2',
}
export interface GetSalesOrderPeriodsResponse {
  salesOrderPeriods: SalesOrderPeriod[];
}

export interface SalesOrderPeriod {
  id: number;
  periodNumber: number;
  name: string;
  orderReceivedBefore: string | Date;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  year?: number;
  periodType: PeriodSystem;
}

export class SalesOrderPeriodWithValue implements SalesOrderPeriod {
  value: number;
  id: number;
  periodNumber: number;
  name: string;
  orderReceivedBefore: string | Date;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  year?: number;
  periodType: PeriodSystem;
}

export interface UpsertOrderLineRequest extends OrderLine {
  year: number;
  cropAidId: number;
}

export interface CreateOrderRequest {
  year: number;
  salesOrderPeriodIds: number[];
  deliveryPreference?: string;
}

export interface CreateOrderResponse {
  id: number;
}

export interface UpsertPreferedDeliveryPeriodsRequest {
  year: number;
  preferedDeliveryPeriods: number[];
}

export interface UpsertNumberOfPeriodsRequest {
  year: number;
  periodSystem: string;
}

export interface GetCustomerPreferencesResponse {
  customerId: number;
  year: number;
  preferedDeliveryPeriods: number[];
  periodSystem: PeriodSystem;
}

export interface OrderLineDialogResult extends OrderLine {
  cropAidId: number;
}

export enum PeriodSystem {
  GBM = 'GBM',
  Horticulture = 'Horticulture',
}

export interface GetCropAidsResponse {
  cropAids: CropAidResponse[];
}

export interface CropAidResponse {
  id: number;
  productId: number;
  productV2Id?: number;
  name: string;
  isActive: boolean;
  unitOfMeasurement: string;
  cropProtection: CropProtectionData;
  source: SourceEnum;
}

export interface CropProtectionData {
  admissions: Admission[];
}

export interface Admission {
  id: number;
  number: string;
}

export interface PeriodOrderPlan {
  periodId: number;
  periodName: string;
  packageCount?: number;
  requisiteAmount: number;
  orderLines: OrderplanOrderLine[];
  orderLinesTotalAmount?: number;
  isDisabled: boolean;
}

export interface PeriodOrderPlanWithDetails {
  periodId: number;
  periodName: string;
  startDispatchFrom: string | Date;
  deliverBefore: string | Date;
  cropAidId: number;
  cropAidName: string;
  packageId: number;
  packageCount?: number;
  packageAmount: number;
  packageUnitOfMeasurement: string;
  requisiteAmount: number;
}

export interface OrderplanOrderLine {
  id: number;
  count: number;
  status: OrderLineStatus;
  addedByOptimizer: boolean;
  shipToId: number;
  orderedDate: string;
  deliveryPeriodMoment: DeliveryPeriodMomentType;
  treatmentType: TreatmentTypes;
  /**
   * Newest version (2022) of tracking orderlines to the cart-service.
   */
  cartId?: number;
  /**
   * Old version (~2020-2021) of tracking orderlines to the checkout-service.
   */
  checkoutId?: number;
  /**
   * Oldest version (~2020) of tracking orderlines to the order-management-service.
   */
  orderedId?: number;
}

export interface GetOrderPlanResponse {
  result: OrderPlanOverviewV2[];
}

export interface OrderPlanOverviewV2 {
  cropAidId: number;
  cropAidName: string;
  orderLineIds?: number[];
  packageId?: number;
  unitOfMeasurement: string;
  customerProductInventoryAmount: number;
  orderLinesTotalAmount: number;
  requisiteAmount: number;
  result?: number;
  packageAmount?: number;
  packageUnitOfMeasurement?: string;
  isMainCropAid?: boolean;
  isPackage?: boolean;
  orderplanPeriods: PeriodOrderPlan[];
  isAccentRowColor?: boolean;
  year: number;
}

export enum OrderplanColumnTypes {
  cropAidName = 'cropAidName',
  customerProductInventoryAmount = 'customerProductInventoryAmount',
  orderLinesTotalAmount = 'orderLinesTotalAmount',
  requisiteAmount = 'requisiteAmount',
  result = 'result',
  v1 = 'v1',
  v2 = 'v2',
  v3 = 'v3',
  v4 = 'v4',
  v5 = 'v5',
  v6 = 'v6',
  v7 = 'v7',
  v8 = 'v8',
  v9 = 'v9',
  v10 = 'v10',
  v11 = 'v11',
  v12 = 'v12',
  p1 = 'p1',
  p2 = 'p2',
  p3 = 'p3',
  p4 = 'p4',
  p5 = 'p5',
  p6 = 'p6',
  p7 = 'p7',
  p8 = 'p8',
  p9 = 'p9',
  p10 = 'p10',
  p11 = 'p11',
  p12 = 'p12',
  p13 = 'p13',
  p14 = 'p14',
  p15 = 'p15',
  p16 = 'p16',
  p17 = 'p17',
  p18 = 'p18',
  p19 = 'p19',
  p20 = 'p20',
  p21 = 'p21',
  p22 = 'p22',
  p23 = 'p23',
  p24 = 'p24',
}

export const deliveryPeriodsToColumnTypesForGBM: {
  [deliveryPeriod: string]: OrderplanColumnTypes;
} = {
  1: OrderplanColumnTypes.v1,
  2: OrderplanColumnTypes.v2,
  3: OrderplanColumnTypes.v3,
  4: OrderplanColumnTypes.v4,
  5: OrderplanColumnTypes.v5,
  6: OrderplanColumnTypes.v6,
  7: OrderplanColumnTypes.v7,
  8: OrderplanColumnTypes.v8,
  9: OrderplanColumnTypes.v9,
  10: OrderplanColumnTypes.v10,
  11: OrderplanColumnTypes.v11,
  12: OrderplanColumnTypes.v12,
};

export const deliveryPeriodsToColumnTypesForHorticulture: {
  [deliveryPeriod: string]: OrderplanColumnTypes;
} = {
  1: OrderplanColumnTypes.p1,
  2: OrderplanColumnTypes.p2,
  3: OrderplanColumnTypes.p3,
  4: OrderplanColumnTypes.p4,
  5: OrderplanColumnTypes.p5,
  6: OrderplanColumnTypes.p6,
  7: OrderplanColumnTypes.p7,
  8: OrderplanColumnTypes.p8,
  9: OrderplanColumnTypes.p9,
  10: OrderplanColumnTypes.p10,
  11: OrderplanColumnTypes.p11,
  12: OrderplanColumnTypes.p12,
  13: OrderplanColumnTypes.p13,
  14: OrderplanColumnTypes.p14,
  15: OrderplanColumnTypes.p15,
  16: OrderplanColumnTypes.p16,
  17: OrderplanColumnTypes.p17,
  18: OrderplanColumnTypes.p18,
  19: OrderplanColumnTypes.p19,
  20: OrderplanColumnTypes.p20,
  21: OrderplanColumnTypes.p21,
  22: OrderplanColumnTypes.p22,
  23: OrderplanColumnTypes.p23,
  24: OrderplanColumnTypes.p24,
};

export interface TableColumnsInfo {
  columnDef: string;
  headerDef: string;
}

export interface StockEditModel {
  amount: string;
  cropAidId: number;
}

export interface DigitalProductSubscription {
  skuName: string;
  shipToId: number;
  customerId: number;
  year: number;
  skuId: number;
  totalRequisite: number;
  correction: number;
  includeInOrder: boolean;
  alreadyOrdered: number;
  unitOfMeasurement: string;
}

export interface GetProductSubscriptionResponse {
  requisites: DigitalProductSubscription[];
}

export interface ModuleActivationResponse {
  isActivated: boolean;
  customerId: number;
  module: string;
}

export interface DigitalProductsDialogResult {
  digitalProductSubscriptions: DigitalProductSubscription[];
}

export interface IndexedDigitalProductSubscription extends DigitalProductSubscription {
  index: number;
}

export interface DigitalProductsToSave{
  skuId: number;
  correction: number;
  includeInOrder: boolean;
}

export interface BemProduct {
  readonly id: string;
  readonly name: string;
  readonly totalOrdered: number;
  readonly unitOfMeasurement: string;
  readonly cropFields: BemProductCropField[];
}

export interface BemProductCropField {
  readonly id: number;
  readonly name: string;
  readonly hectares: number;
  readonly cropVariety: string;
  readonly orderNumber?: string;
  readonly orderDate?: Date;
}
